/**
 * OpenAI Task Suggestions Service
 * Provides AI-powered suggestions for all task types in Visibeen
 */

import OpenAIReviewService from './GeminiAIReviewService';

import logger from '../utils/logger';
class OpenAITaskService {
  constructor() {
    this.reviewService = OpenAIReviewService;
  }

  /**
   * Generate AI suggestions based on task type
   * @param {Object} task - Task object
   * @param {Object} context - Additional context (businessInfo, reviews, etc.)
   * @returns {Promise<Object>} - AI suggestions
   */
  async generateTaskSuggestions(task, context = {}) {
    try {
      const taskType = this.identifyTaskType(task);
      logger.log(`🤖 [AI Task] Generating suggestions for: ${taskType}`);

      switch (taskType) {
        case 'review_reply':
          return await this.generateReviewReplySuggestions(task, context);
        
        case 'post_creation':
          return await this.generatePostSuggestions(task, context);
        
        case 'business_description':
          return await this.generateDescriptionSuggestions(task, context);
        
        case 'category_selection':
          return await this.generateCategorySuggestions(task, context);
        
        case 'photo_upload':
          return await this.generatePhotoSuggestions(task, context);
        
        case 'website_update':
          return await this.generateWebsiteSuggestions(task, context);
        
        case 'business_hours':
          return await this.generateBusinessHoursSuggestions(task, context);
        
        case 'social_media':
          return await this.generateSocialMediaSuggestions(task, context);
        
        case 'qna_setup':
          return await this.generateQnASuggestions(task, context);
        
        default:
          return await this.generateGenericSuggestions(task, context);
      }
    } catch (error) {
      logger.error('❌ Error generating task suggestions:', error);
      return this.getFallbackSuggestions(task);
    }
  }

  /**
   * Identify task type from task title/description
   */
  identifyTaskType(task) {
    const text = `${task.title} ${task.description || ''}`.toLowerCase();
    
    if (text.includes('review') || text.includes('reply')) return 'review_reply';
    if (text.includes('post') || text.includes('update')) return 'post_creation';
    if (text.includes('description')) return 'business_description';
    if (text.includes('category') || text.includes('categories')) return 'category_selection';
    if (text.includes('photo') || text.includes('image')) return 'photo_upload';
    if (text.includes('website')) return 'website_update';
    if (text.includes('hours') || text.includes('timing')) return 'business_hours';
    if (text.includes('social media')) return 'social_media';
    if (text.includes('q&a') || text.includes('question')) return 'qna_setup';
    
    return 'generic';
  }

  /**
   * Generate review reply suggestions
   */
  async generateReviewReplySuggestions(task, context) {
    const { reviews = [], businessInfo = {} } = context;
    const unrepliedReviews = reviews.filter(r => !r.reviewReply?.comment);

    const suggestions = {
      type: 'review_reply',
      title: '💬 AI Review Reply Suggestions',
      actionItems: [],
      tips: [
        'Respond within 24 hours for best engagement',
        'Personalize each reply with customer name',
        'Thank positive reviews and address negative ones professionally',
        'Keep replies concise (50-150 words)'
      ],
      stats: {
        totalReviews: reviews.length,
        unreplied: unrepliedReviews.length,
        averageRating: reviews.length > 0 
          ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
          : 0
      }
    };

    // Generate AI replies for up to 3 unreplied reviews
    for (const review of unrepliedReviews.slice(0, 3)) {
      try {
        const tone = review.rating >= 4 ? 'grateful' : review.rating === 3 ? 'professional' : 'apologetic';
        const aiReply = await this.reviewService.generateReviewReply(review, businessInfo, tone);
        
        suggestions.actionItems.push({
          review: {
            author: review.author,
            rating: review.rating,
            text: review.text
          },
          suggestedReply: aiReply,
          confidence: 'high'
        });
      } catch (error) {
        logger.warn('Failed to generate reply for review:', error);
      }
    }

    return suggestions;
  }

  /**
   * Generate GMB post content suggestions
   */
  async generatePostSuggestions(task, context) {
    const { businessInfo = {}, recentPosts = [] } = context;
    
    const postTypes = ['Update', 'Offer', 'Event', 'Product'];
    const suggestions = {
      type: 'post_creation',
      title: '📝 AI Post Content Suggestions',
      actionItems: [],
      tips: [
        'Post at least once per week for best visibility',
        'Include high-quality images with every post',
        'Add a clear call-to-action button',
        'Keep posts between 100-300 words',
        'Use relevant keywords naturally'
      ],
      stats: {
        postsThisMonth: recentPosts.length,
        recommendedFrequency: 'Weekly'
      }
    };

    // Generate post ideas
    for (const postType of postTypes) {
      const postIdea = await this.generatePostContent(postType, businessInfo);
      suggestions.actionItems.push(postIdea);
    }

    return suggestions;
  }

  /**
   * Generate post content based on type
   */
  async generatePostContent(postType, businessInfo) {
    const { name = 'our business', category = 'business' } = businessInfo;
    
    const templates = {
      'Update': {
        title: `New at ${name}!`,
        content: `We're excited to share some updates with our valued customers! Check out what's new and visit us soon. Your satisfaction is our priority.`,
        cta: 'Learn More',
        confidence: 'medium'
      },
      'Offer': {
        title: `Special Offer from ${name}`,
        content: `Limited time offer! Don't miss out on our exclusive deals. Visit us today to take advantage of special pricing on select services.`,
        cta: 'Get Offer',
        confidence: 'high'
      },
      'Event': {
        title: `Upcoming Event at ${name}`,
        content: `Join us for an exciting event! We're hosting a special gathering for our community. Mark your calendars and bring your friends!`,
        cta: 'RSVP Now',
        confidence: 'medium'
      },
      'Product': {
        title: `Featured ${category} from ${name}`,
        content: `Discover our premium offerings! We take pride in delivering quality products and exceptional service. Come see what makes us special.`,
        cta: 'View Products',
        confidence: 'medium'
      }
    };

    return templates[postType] || templates['Update'];
  }

  /**
   * Generate business description suggestions
   */
  async generateDescriptionSuggestions(task, context) {
    const { businessInfo = {} } = context;
    
    try {
      const aiDescription = await this.reviewService.generateBusinessDescription(businessInfo);
      
      return {
        type: 'business_description',
        title: '📋 AI Business Description',
        actionItems: [{
          suggestion: aiDescription,
          confidence: 'high',
          wordCount: aiDescription.split(' ').length,
          seoScore: this.calculateSEOScore(aiDescription, businessInfo)
        }],
        tips: [
          'Keep description between 150-300 words',
          'Include primary keywords naturally',
          'Mention your location and service area',
          'Highlight what makes you unique',
          'Add a clear call-to-action at the end'
        ]
      };
    } catch (error) {
      return this.getFallbackDescriptionSuggestions(businessInfo);
    }
  }

  /**
   * Generate category suggestions
   */
  async generateCategorySuggestions(task, context) {
    const { businessInfo = {} } = context;
    const { name = '', category = '' } = businessInfo;

    return {
      type: 'category_selection',
      title: '🏷️ AI Category Recommendations',
      actionItems: [
        {
          primary: category || 'Business',
          additional: this.getAdditionalCategories(name, category),
          confidence: 'high'
        }
      ],
      tips: [
        'Choose the most specific category that describes your business',
        'Add up to 9 additional categories',
        'Categories improve discoverability in searches',
        'Update categories if your services change'
      ],
      stats: {
        currentCategories: category ? 1 : 0,
        recommended: 5
      }
    };
  }

  /**
   * Get additional category suggestions
   */
  getAdditionalCategories(businessName, primaryCategory) {
    const categoryMap = {
      'Electronics': ['Electronics Store', 'Electronics Repair', 'Appliance Store', 'Computer Store'],
      'Restaurant': ['Restaurant', 'Fast Food', 'Cafe', 'Bar & Grill'],
      'Dental': ['Dentist', 'Orthodontist', 'Cosmetic Dentist', 'Pediatric Dentist'],
      'Automotive': ['Auto Repair', 'Car Dealer', 'Auto Parts', 'Car Wash'],
      'Real Estate': ['Real Estate Agency', 'Property Management', 'Real Estate Appraiser'],
      'Retail': ['Retail Store', 'Clothing Store', 'Gift Shop', 'Convenience Store']
    };

    for (const [key, categories] of Object.entries(categoryMap)) {
      if (businessName.toLowerCase().includes(key.toLowerCase()) || 
          primaryCategory?.toLowerCase().includes(key.toLowerCase())) {
        return categories;
      }
    }

    return ['General Business', 'Service Provider', 'Local Business', 'Retail'];
  }

  /**
   * Generate photo upload suggestions
   */
  async generatePhotoSuggestions(task, context) {
    const { businessInfo = {}, currentPhotos = [] } = context;

    return {
      type: 'photo_upload',
      title: '📸 AI Photo Strategy',
      actionItems: [
        {
          category: 'Exterior',
          description: 'Storefront, building facade, parking area',
          priority: 'High',
          minPhotos: 3,
          tips: 'Take photos during golden hour (sunrise/sunset) for best lighting'
        },
        {
          category: 'Interior',
          description: 'Reception area, workspace, amenities',
          priority: 'High',
          minPhotos: 5,
          tips: 'Ensure good lighting and clean, organized spaces'
        },
        {
          category: 'Products/Services',
          description: 'Showcase your offerings',
          priority: 'Medium',
          minPhotos: 10,
          tips: 'Use high-resolution images with proper focus'
        },
        {
          category: 'Team',
          description: 'Staff photos, team at work',
          priority: 'Medium',
          minPhotos: 3,
          tips: 'Professional yet friendly photos build trust'
        },
        {
          category: 'Logo/Branding',
          description: 'Logo, branded materials, signage',
          priority: 'High',
          minPhotos: 2,
          tips: 'Clear, high-resolution logo with transparent background'
        }
      ],
      tips: [
        'Upload at least 25 photos for maximum visibility',
        'Update photos every 2-3 months',
        'Use natural lighting when possible',
        'Show your business in action',
        'Avoid heavily filtered or edited photos'
      ],
      stats: {
        currentPhotos: currentPhotos.length,
        recommended: 25,
        completion: Math.round((currentPhotos.length / 25) * 100)
      }
    };
  }

  /**
   * Generate website update suggestions
   */
  async generateWebsiteSuggestions(task, context) {
    const { businessInfo = {} } = context;
    const { website } = businessInfo;

    return {
      type: 'website_update',
      title: '🌐 Website Optimization Tips',
      actionItems: [
        {
          item: 'Add/Update Website URL',
          status: website ? 'Complete' : 'Pending',
          importance: 'Critical',
          description: website ? `Current: ${website}` : 'No website URL added'
        },
        {
          item: 'Mobile Responsiveness',
          importance: 'High',
          description: 'Ensure website works perfectly on mobile devices'
        },
        {
          item: 'Contact Information',
          importance: 'High',
          description: 'Display phone, email, and address prominently'
        },
        {
          item: 'Call-to-Action Buttons',
          importance: 'Medium',
          description: 'Add clear CTAs like "Book Now", "Contact Us", "Get Quote"'
        },
        {
          item: 'Business Hours',
          importance: 'Medium',
          description: 'Show current hours and holiday schedules'
        }
      ],
      tips: [
        'Your website should load in under 3 seconds',
        'Include customer reviews/testimonials',
        'Add high-quality photos of your business',
        'Ensure SSL certificate for security (https)',
        'Optimize for local SEO with location keywords'
      ]
    };
  }

  /**
   * Generate business hours suggestions
   */
  async generateBusinessHoursSuggestions(task, context) {
    const { businessInfo = {} } = context;

    return {
      type: 'business_hours',
      title: '⏰ Business Hours Recommendations',
      actionItems: [
        {
          suggestion: 'Set regular hours for each day of the week',
          priority: 'High'
        },
        {
          suggestion: 'Add special hours for holidays',
          priority: 'Medium'
        },
        {
          suggestion: 'Enable "Open 24 hours" if applicable',
          priority: 'Medium'
        },
        {
          suggestion: 'Add "More Hours" for specific services',
          priority: 'Low'
        }
      ],
      tips: [
        'Update hours before holidays and special events',
        'Mark as "Temporarily Closed" if needed',
        'Consistent hours build customer trust',
        'Consider adding holiday hours 2 weeks in advance'
      ],
      bestPractices: [
        'Monday-Friday: 9:00 AM - 6:00 PM',
        'Saturday: 10:00 AM - 4:00 PM',
        'Sunday: Closed or limited hours'
      ]
    };
  }

  /**
   * Generate social media suggestions
   */
  async generateSocialMediaSuggestions(task, context) {
    const { businessInfo = {} } = context;

    return {
      type: 'social_media',
      title: '📱 Social Media Integration',
      actionItems: [
        {
          platform: 'Facebook',
          priority: 'High',
          benefits: 'Largest social network, great for local businesses',
          action: 'Link your Facebook business page'
        },
        {
          platform: 'Instagram',
          priority: 'High',
          benefits: 'Visual platform, perfect for photos and stories',
          action: 'Connect Instagram business account'
        },
        {
          platform: 'Twitter',
          priority: 'Medium',
          benefits: 'Real-time updates and customer service',
          action: 'Link Twitter profile for updates'
        },
        {
          platform: 'LinkedIn',
          priority: 'Medium',
          benefits: 'Professional networking and B2B connections',
          action: 'Add LinkedIn company page'
        }
      ],
      tips: [
        'Post consistently across all platforms',
        'Use the same profile photo across platforms',
        'Cross-promote content between platforms',
        'Respond to comments and messages promptly',
        'Use platform-specific hashtags'
      ]
    };
  }

  /**
   * Generate Q&A setup suggestions
   */
  async generateQnASuggestions(task, context) {
    const { businessInfo = {} } = context;
    const { category = 'business' } = businessInfo;

    return {
      type: 'qna_setup',
      title: '❓ Q&A Strategy',
      actionItems: this.getCommonQuestions(category),
      tips: [
        'Answer questions within 24 hours',
        'Be specific and helpful in responses',
        'Include keywords naturally in answers',
        'Preemptively add common questions',
        'Monitor and respond to all new questions'
      ],
      benefits: [
        'Reduces incoming phone calls',
        'Improves SEO and visibility',
        'Builds trust with potential customers',
        'Addresses common concerns upfront'
      ]
    };
  }

  /**
   * Get common questions by category
   */
  getCommonQuestions(category) {
    const questions = {
      'Restaurant': [
        { q: 'Do you offer delivery?', a: 'Yes, we offer delivery through our website and third-party apps.' },
        { q: 'Do you have vegetarian options?', a: 'Yes, we have a variety of vegetarian dishes on our menu.' },
        { q: 'Can I make a reservation?', a: 'Yes, you can book a table through our website or by calling us.' }
      ],
      'Dentist': [
        { q: 'Do you accept insurance?', a: 'Yes, we accept most major insurance plans. Please call to verify.' },
        { q: 'Do you offer emergency services?', a: 'Yes, we provide emergency dental services. Please call immediately.' },
        { q: 'What are your office hours?', a: 'We are open Monday-Friday 9 AM-6 PM, Saturday 10 AM-2 PM.' }
      ],
      'Electronics': [
        { q: 'Do you offer warranty?', a: 'Yes, all products come with manufacturer warranty plus our service guarantee.' },
        { q: 'Do you provide installation?', a: 'Yes, we offer professional installation services for all products.' },
        { q: 'What payment methods do you accept?', a: 'We accept cash, credit cards, debit cards, and digital payments.' }
      ]
    };

    return questions[category] || [
      { q: 'What are your business hours?', a: 'Please check our profile for current hours.' },
      { q: 'Where are you located?', a: 'Our address is listed on our Google Business Profile.' },
      { q: 'How can I contact you?', a: 'You can call us or visit during business hours.' }
    ];
  }

  /**
   * Generate generic task suggestions
   */
  async generateGenericSuggestions(task, context) {
    return {
      type: 'generic',
      title: '💡 Task Recommendations',
      actionItems: [
        {
          suggestion: 'Break down this task into smaller, actionable steps',
          priority: 'High'
        },
        {
          suggestion: 'Set a realistic deadline based on task complexity',
          priority: 'Medium'
        },
        {
          suggestion: 'Document your progress and results',
          priority: 'Medium'
        }
      ],
      tips: [
        'Focus on completing one task at a time',
        'Track your progress regularly',
        'Ask for help if you get stuck',
        'Celebrate small wins along the way'
      ]
    };
  }

  /**
   * Calculate SEO score for business description
   */
  calculateSEOScore(description, businessInfo) {
    let score = 0;
    const { name = '', category = '', location = '' } = businessInfo;
    
    // Check for business name
    if (description.includes(name)) score += 20;
    
    // Check for category keywords
    if (category && description.toLowerCase().includes(category.toLowerCase())) score += 20;
    
    // Check for location
    if (location && description.includes(location)) score += 20;
    
    // Check word count (150-300 is optimal)
    const wordCount = description.split(' ').length;
    if (wordCount >= 150 && wordCount <= 300) score += 20;
    
    // Check for call-to-action words
    const ctaWords = ['visit', 'call', 'contact', 'book', 'schedule', 'discover', 'explore'];
    if (ctaWords.some(word => description.toLowerCase().includes(word))) score += 20;
    
    return Math.min(score, 100);
  }

  /**
   * Fallback suggestions when AI fails
   */
  getFallbackSuggestions(task) {
    return {
      type: 'fallback',
      title: '💡 Task Guidance',
      actionItems: [
        {
          suggestion: 'Review the task requirements carefully',
          priority: 'High'
        },
        {
          suggestion: 'Check similar completed tasks for reference',
          priority: 'Medium'
        },
        {
          suggestion: 'Break the task into smaller steps',
          priority: 'Medium'
        }
      ],
      tips: [
        'Take your time to understand the task fully',
        'Document your approach and results',
        'Ask for clarification if needed'
      ]
    };
  }

  /**
   * Fallback description suggestions
   */
  getFallbackDescriptionSuggestions(businessInfo) {
    const { name = 'Our business', category = 'business' } = businessInfo;
    
    return {
      type: 'business_description',
      title: '📋 Description Template',
      actionItems: [{
        suggestion: `${name} is a trusted ${category} committed to providing exceptional service to our customers. With years of experience and a dedicated team, we deliver quality results that exceed expectations. Visit us today to discover why our customers choose us for their needs.`,
        confidence: 'medium',
        wordCount: 45,
        seoScore: 60
      }],
      tips: [
        'Customize this template with your unique selling points',
        'Add specific services or products you offer',
        'Include your location and service area',
        'End with a clear call-to-action'
      ]
    };
  }
}

// Export singleton instance
export default new OpenAITaskService();
